import { useEffect, useMemo, useState } from "react";
import useWidget from "./useWidget";
import { Button } from "V2.0/common/button";
import ChatEmbed from "../ChatEmbed/ChatEmbed";
import { Dialog, DialogTitle, DialogBody } from "V2.0/common/dialog";
import useWhitelistDomain from "../ChatEmbed/useWhitelistDomain";
import WhitelistedDomainsTable from "../ChatEmbed/WhitelistedDomainsTable";
import {
  ChatBubbleLeftEllipsisIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import { message } from "antd";
import Domains from "../ChatEmbed/Domains";
import { FaLaptop } from "react-icons/fa6";

function getChatWidgetCode(workspaceId, assistantId) {
  return `
  <script>
  (function (w, d, s, o, f, js, fjs) {
   w[o] =
   w[o] ||
   function () {
       (w[o].q = w[o].q || []).push(arguments);
   };
  (js = d.createElement(s)), (fjs = d.getElementsByTagName(s)[0]);
  js.id = o;
  js.src = f;
  js.async = 1;
  fjs.parentNode.insertBefore(js, fjs);
  })(window, document, "script", "_hw", "https://${window.location.hostname}/widget.js");
  _hw("init", { debug: true, workspaceId: "${workspaceId}", assistantId: "${assistantId}"  });
  </script>         
  `;
}

function getFullPageEmbedCode(workspaceId, assistantId) {
  return `<iframe id="super_embed" style="height:100dvh; width:100vw" src="https://${window.location.hostname}/embed?workspaceId=${workspaceId}&assistantId=${assistantId}"></iframe>`;
}

function WidgetConfiguration(props) {
  const { open, onClose, children } = props;

  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <DialogTitle>Customize Widget</DialogTitle>
      <DialogBody>{children}</DialogBody>
    </Dialog>
  );
}

function Widget(props) {
  const { workspaceId, assistantId, locked } = props;
  const { data, isLoading, isCreated, createNewWidget, fetchWidget } =
    useWidget(workspaceId, assistantId);
  const {
    data: whitelistDomainData,
    isLoading: isWhitelistDomainLoading,
    isCreated: isWhitelistDomainCreated,
    loading,
    handleUpdateDomainIdentifiers,
    handleCreateDomainIdentifier,
  } = useWhitelistDomain(workspaceId, assistantId);

  const [openWidgetConfiguration, setOpenConfiguration] = useState(false);

  const chatWidgetCode = useMemo(() => {
    return getChatWidgetCode(workspaceId, assistantId);
  }, [workspaceId, assistantId]);

  const fullPageEmbedCode = useMemo(() => {
    return getFullPageEmbedCode(workspaceId, assistantId);
  }, [workspaceId, assistantId]);

  useEffect(() => {
    fetchWidget(true);
  }, []);

  useEffect(() => {
    if (!isLoading && !isCreated) {
      createNewWidget();
    }
  }, [isLoading, isCreated]);

  const handleCopy = (item, title) => {
    navigator.clipboard
      .writeText(item)
      .then(() => {
        message.success(`${title} copied`);
      })
      .catch((err) => {
        message.error("Failed to copy the embed code");
        console.error("Failed to copy: ", err);
      });
  };

  if (isLoading || !isCreated || isWhitelistDomainLoading) {
    return null;
  }

  return (
    <>
      <div className="flex p-8 w-2/5 flex-col gap-y-4 mx-auto h-full min-h-0">
        <div className="flex justify-between">
          <div className="flex">
            <p className="font-semibold text-base">Widget Settings</p>
          </div>
          <div className="flex-none gap-x-2">
              <Button
                outline
                onClick={() => setOpenConfiguration(true)}
                disabled={locked}
              >
                <Cog6ToothIcon /> Customize Widget
              </Button>
          </div>
        </div>
        <div className="flex flex-col justify-center gap-y-2">
          <div className="flex items-center justify-between  border rounded-lg p-2">
            <div className="flex items-center gap-x-2">
              <ChatBubbleLeftEllipsisIcon className="size-4" />
              <p className="text-sm font-semibold">Chat Widget Code</p>
            </div>
            <Button
              plain
              disabled={locked}
              onClick={() => handleCopy(chatWidgetCode, "Chat Widget")}
            >
              <DocumentDuplicateIcon /> Copy
            </Button>
          </div>
          <div className="flex items-center justify-between  border rounded-lg p-2">
            <div className="flex items-center gap-x-2">
              <FaLaptop className="size-4" />
              <p className="text-sm font-semibold">Full Page Embed Code</p>
            </div>

            <Button
              plain
              disabled={locked}
              onClick={() => handleCopy(fullPageEmbedCode, "Full Page Embed")}
            >
              <DocumentDuplicateIcon /> Copy
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-y-4 h-full min-h-0 border rounded-lg p-4">
          <div className="flex items-center gap-x-2">
            <GlobeAltIcon className="size-4" />
            <p className="text-sm font-semibold">
              Domains ({whitelistDomainData.domainIdentifiers.length})
            </p>
          </div>
          <Domains
            domainIdentifiers={whitelistDomainData.domainIdentifiers}
            handleAddDomainIdentifier={
              isWhitelistDomainCreated
                ? handleUpdateDomainIdentifiers
                : handleCreateDomainIdentifier
            }
            locked={locked}
          />
          {Boolean(whitelistDomainData.domainIdentifiers.length) ? (
            <div className="flex-1 h-full min-h-0 overflow-auto">
              <WhitelistedDomainsTable
                domainIdentifiers={whitelistDomainData.domainIdentifiers}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col gap-y-4">
                <p className="text-sm text-gray-600">No Domains added</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {openWidgetConfiguration && (
        <WidgetConfiguration
          open={openWidgetConfiguration}
          onClose={() => setOpenConfiguration(false)}
        >
          <ChatEmbed
            data={data}
            fetchWidget={fetchWidget}
            onClose={() => setOpenConfiguration(false)}
            workspaceId={workspaceId}
            assistantId={assistantId}
          />
        </WidgetConfiguration>
      )}
    </>
  );
}

export default Widget;
